// $login_email / $login_password (and numbered $login1_email, … variants) are LOGIN-CREDENTIAL tokens:
// placeholders the runner substitutes with a real secret the project supplied as a global variable
// (login_mode 'provided'). They are the one class of $token that must NEVER be fabricated — a made-up
// value is a fake credential, and typing one into a login/auth form is a state-changing guess, not test
// data. When such a token is referenced but the project supplied no matching global (e.g. login_mode
// 'none'), it is left unresolved so the runner reports the auth wall as the obstacle instead of typing
// an invented email/password.
//
// Declared in this leaf module (not steps.ts) so the startup-session policy in auth-session.ts can match
// the same tokens without importing the whole step-execution module graph.

const LOGIN_FIELDS = ['email', 'password', 'username', 'mode', 'status', 'tokens'] as const

/** A field of a `login{numeral}_{field}` project variable. */
export type LoginField = (typeof LOGIN_FIELDS)[number]

/** The fields of a login-credential token. */
export type CredentialField = Extract<LoginField, 'email' | 'password'>

/** A parsed `login{numeral}_{field}` project variable name. */
export interface LoginVariable {
  /** The digits as written: '' for the unnumbered primary (`login_email`). */
  numeral: string
  /** The identity it belongs to: `login_` and `login1_` are both slot 1. */
  slot: number
  field: LoginField
}

const LOGIN_VARIABLE = /^login(\d*)_([a-z]+)$/

export function parseLoginVariable(name: string): LoginVariable | null {
  const match = LOGIN_VARIABLE.exec(name)
  const field = match && LOGIN_FIELDS.find(field => field === match[2])
  if (!field) return null
  const numeral = match[1]!
  return { numeral, slot: numeral ? parseInt(numeral, 10) : 1, field }
}

/** The `login{numeral}_{field}` name: the inverse of parseLoginVariable. */
export function loginVariableName(numeral: string, field: LoginField): string {
  return `login${numeral}_${field}`
}

function isCredentialField(field: LoginField): field is CredentialField {
  return field === 'email' || field === 'password'
}

/** A parsed login-credential token name. */
export interface CredentialVariable extends LoginVariable {
  field: CredentialField
}

export function parseCredentialToken(name: string): CredentialVariable | null {
  const login = parseLoginVariable(name)
  return login && isCredentialField(login.field) ? { ...login, field: login.field } : null
}

const CREATED_ACCOUNT_FALLBACK_NUMERAL = '2'

/** The numeral of the slot a created account is saved to: the primary, unless its email holds a different address. */
export function promotionSlot(vars: readonly { name: string; init?: string }[], email: string): string {
  const existingEmail = vars.find((v) => v.name === loginVariableName('', 'email'))?.init ?? ''
  return existingEmail && existingEmail !== email ? CREATED_ACCOUNT_FALLBACK_NUMERAL : ''
}

function hasLoginField(name: string, fields: readonly LoginField[]): boolean {
  const login = parseLoginVariable(name)
  return !!login && fields.includes(login.field)
}

/** A login-credential token: a supplied secret that must never be fabricated. */
export function isCredentialToken(name: string): boolean {
  return !!parseCredentialToken(name)
}

/** A login-credential token of `field`. */
export function isCredentialTokenOf(name: string, field: CredentialField): boolean {
  return parseLoginVariable(name)?.field === field
}

/** A project login credential (username included): supplied per run, never bound by the steps that use it. */
export function isLoginCredentialName(name: string): boolean {
  return hasLoginField(name, ['email', 'username', 'password'])
}

/** A login identifier: the email or username an account signs in with. */
export function isLoginIdentifierName(name: string): boolean {
  return hasLoginField(name, ['email', 'username'])
}

/** Login control state the runner reads from the globals: never a value to type or initialize. */
export function isLoginControlState(name: string): boolean {
  return hasLoginField(name, ['mode', 'status'])
}

/** The project's login-mode setting. */
export function isLoginModeName(name: string): boolean {
  return hasLoginField(name, ['mode'])
}

// A generator expression embedded in template form (`qa-${random()}@audos.com`): fresh-test-data
// authoring shaped by the author's literal text, which the runner generates at execution — never a
// literal value to type, store or bind verbatim. Bug Report #20814: a value-capture read_variable
// carried `qa-${random()}@audos.com`, the bind path stored the unsubstituted template as the
// variable's value, the corrupted literal was typed into the settings email field, and the recovery
// the corruption forced could not persist — the run stopped one step before the checkpoint it
// existed to measure. Declared in this leaf module (which variable-init imports) so every site that
// special-cases the pure `random()` / `timestamp()` forms shares one definition of the template form.
export const GENERATOR_TEMPLATE_SOURCE = String.raw`\$\{\s*(random\(\)|timestamp\(\))\s*\}`

const GENERATOR_TEMPLATE = new RegExp(GENERATOR_TEMPLATE_SOURCE)

/** Whether a value embeds a generator expression in template form (`${random()}` / `${timestamp()}`). */
export function hasGeneratorTemplate(text: string): boolean {
  return GENERATOR_TEMPLATE.test(text)
}

// Marks a journey write refused because a credential it uses is not provided; shared by server and authoring.
export const UNUSABLE_CREDENTIALS = 'unusable-credentials'

// The second login identity is generated provisional; a pass of its own journey, of this origin, confirms it.
export const SECOND_IDENTITY_ORIGIN = 'additional-login-identity:2'
export const SECOND_IDENTITY_NUMERAL = '2'
export const SECOND_IDENTITY_CREDENTIALS: readonly string[] = [loginVariableName(SECOND_IDENTITY_NUMERAL, 'email'), loginVariableName(SECOND_IDENTITY_NUMERAL, 'password')]

/** Whether a step may store into a credential target: a read or a supplied value, never a generated one. */
export function storesCredential(step: { type?: unknown; value?: unknown }): boolean {
  if (step.type !== 'read_variable') return false
  const value = step.value
  // A generator in either form — pure (`random()`) or embedded in a template (`pass-${random()}`) —
  // fabricates the credential rather than supplying it, so it is no credential store.
  return !(value === 'random()' || value === 'timestamp()' || (typeof value === 'string' && hasGeneratorTemplate(value)))
}

/** The uses no single variable set resolves: none when one set resolves them all, else those the first set lacks. */
export function unresolvedCredentials(uses: readonly string[], sets: readonly (readonly string[])[]): string[] {
  const missing = sets.map(names => uses.filter(name => !names.includes(name)))
  return missing.some(names => !names.length) ? [] : missing[0] ?? [...uses]
}
